import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { buildWhatsAppUrl } from '@/lib/whatsapp'
import { WhatsAppIcon } from '@/components/shop/shop-bits'

interface WhatsAppLinkProps {
    // Builds the message. Called with an empty origin for the first render (server and hydration),
    // then again after mount with the real site origin so page links can be included.
    message: (origin: string) => string
    children: ReactNode
    className?: string
    variant?: 'solid' | 'outline' | 'ghost'
    size?: 'md' | 'lg' | 'sm'
    onClick?: () => void
}

// A real link to WhatsApp with a pre-filled message. The href is refreshed after mount so it can
// include the full page address without a server/client mismatch.
export const WhatsAppLink: FC<WhatsAppLinkProps> = ({ message, children, className, variant = 'solid', size = 'md', onClick }): ReactNode => {
    const [href, setHref] = useState(() => buildWhatsAppUrl(message('')))

    useEffect(() => {
        setHref(buildWhatsAppUrl(message(window.location.origin)))
    }, [message])

    return (
        <a
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            onClick={onClick}
            className={cn(
                'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2',
                size === 'lg' && 'h-14 px-7 text-base',
                size === 'md' && 'h-12 px-6 text-sm',
                size === 'sm' && 'h-10 px-4 text-sm',
                variant === 'solid' && 'bg-sage-700 text-cream-50 shadow-[0_10px_30px_-12px_rgba(59,79,55,0.7)] hover:-translate-y-0.5 hover:bg-sage-600',
                variant === 'outline' && 'border border-sage-600/40 bg-white/70 text-sage-700 hover:border-sage-600 hover:bg-sage-50',
                variant === 'ghost' && 'text-sage-700 hover:bg-sage-50',
                className
            )}
        >
            <WhatsAppIcon className={size === 'sm' ? 'size-4' : 'size-5'} />
            {children}
        </a>
    )
}
